import { z } from "zod";

import { ImpulseFormShape, ImpulseFormSwitch, ImpulseFormUnit } from "~/tools/impulse-form";
import { isStringDirty } from "~/tools/is-string-dirty";

import {
	type DashboardVariable,
	type DashboardVariableInitConfig,
	type DashboardVariableKind,
	dashboardVariableCommonSchema,
} from "../../common/dashboard-spec";

import type { FormProcessor } from "./_form-processor";
import { ListVariableFormProcessor, type ListVariableFormState } from "./_list-variable";
import { TextVariableFormProcessor, type TextVariableFormState } from "./_text-variable";

export type FormState = ImpulseFormShape<{
	name: ImpulseFormUnit<string, ReadonlyArray<string>>;
	label: ImpulseFormUnit<string, ReadonlyArray<string>, undefined | string>;
	description: ImpulseFormUnit<string, ReadonlyArray<string>, undefined | string>;

	type: ImpulseFormSwitch<
		ImpulseFormUnit<string, ReadonlyArray<string>, DashboardVariableKind>,
		{
			TextVariable: TextVariableFormState;

			ListVariable: ListVariableFormState;
		}
	>;
}>;

const displaySchema = dashboardVariableCommonSchema.shape.display.removeCatch().unwrap();

export const VARIABLE_TYPE_OPTIONS = [
	"TextVariable",
	"ListVariable",
] as const satisfies ReadonlyArray<DashboardVariableKind>;

export const Processor: FormProcessor<FormState, undefined | DashboardVariable, DashboardVariableInitConfig> = {
	fromSpec: (variable) =>
		ImpulseFormShape({
			name: ImpulseFormUnit(variable?.name ?? "", {
				schema: dashboardVariableCommonSchema.shape.name,
				isInputDirty: isStringDirty,
			}),
			label: ImpulseFormUnit(variable?.label ?? "", {
				schema: displaySchema.shape.name,
				isInputDirty: isStringDirty,
			}),
			description: ImpulseFormUnit(variable?.description ?? "", {
				schema: displaySchema.shape.description,
				isInputDirty: isStringDirty,
			}),

			type: ImpulseFormSwitch(
				ImpulseFormUnit(variable?.spec.kind ?? "TextVariable", {
					schema: z.enum(VARIABLE_TYPE_OPTIONS),
				}),
				{
					TextVariable: TextVariableFormProcessor.fromSpec(
						variable?.spec.kind === "TextVariable" ? variable.spec.spec : undefined,
					),

					ListVariable: ListVariableFormProcessor.fromSpec(
						variable?.spec.kind === "ListVariable" ? variable.spec.spec : undefined,
					),
				},
			),
		}),

	toSpec: ({ name, label, description, type }) => ({
		name,
		label,
		description,
		spec:
			type.kind === "TextVariable"
				? { kind: "TextVariable", spec: TextVariableFormProcessor.toSpec(type.value) }
				: { kind: "ListVariable", spec: ListVariableFormProcessor.toSpec(type.value) },
	}),
};
